import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Star, ChevronDown } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { assetUrl } from "@/lib/api";

export const DEFAULT_TESTIMONIALS = [
  {
    excerpt: "Our wedding day was more than we could have ever imagined.",
    quote:
      "We thought we could handle everything ourselves, but thank God we met Donodidio. She made the whole process of planning our wedding fun and stress-free. Donodidio was organised, supportive and had everything under control with a smile. Our wedding day was more than we could have ever imagined. We highly recommend her services.",
    name: "Dianadra",
    eventType: "Wedding",
    photo: "/images/testimonial-dianadra.jpg",
    focus: "50% 24%",
    rating: 5,
  },
  {
    excerpt: "You brought life, love, and calm all in one.",
    quote:
      "I just wanted to say a huge THANK YOU for everything you did for my Traditional Wedding - Ghana x Nigeria style! You honestly went above and beyond. From taking on my last-minute enquiry to showing up and showing out, you truly delivered for reallll! You made me feel so comfortable and at ease the whole time. I got to actually enjoy every moment, from morningggg to eveningggg knowing you had everything handled with such grace. Your energy and good vibes were unmatched - you brought life, love, and calm all in one. + Always present and proper well organised!!",
    name: "Abigail A.",
    eventType: "Traditional Wedding · Ghana × Nigeria",
    photo: "/images/testimonial-abigail.jpg",
    focus: "50% 28%",
    rating: 5,
  },
];

function TestimonialCard({ t, active, onActivate }) {
  const [open, setOpen] = useState(false);
  const handleClick = () => {
    if (!active) {
      onActivate();
      return;
    }
    setOpen((o) => !o);
  };
  return (
    <div
      className={`transition-all duration-700 ease-out ${active ? "opacity-100 scale-100" : "opacity-55 scale-[0.96]"}`}
    >
      <button
        data-testid="testimonial-card"
        onClick={handleClick}
        aria-expanded={open}
        className="group block w-full text-left cursor-pointer"
      >
        <figure className="editorial-photo relative aspect-[4/5]">
          <img src={assetUrl(t.photo)} alt={`${t.name} — DonoDidio client`} loading="lazy" style={{ objectPosition: t.focus || "50% 30%" }} />
          <span className="editorial-grade" />
          <div className="absolute inset-x-0 bottom-0 p-6 md:p-7 bg-gradient-to-t from-black/60 via-black/20 to-transparent">
            <p className="font-serif-lux italic text-[#F5F1E8] text-lg md:text-xl leading-snug">
              &ldquo;{t.excerpt}&rdquo;
            </p>
          </div>
        </figure>

        <div className="text-center mt-6">
          <p className="font-serif-lux text-[#47562F] text-2xl">{t.name}</p>
          <p className="text-[0.7rem] uppercase tracking-[0.24em] text-[#B8934A] mt-1.5">{t.eventType}</p>
          <span className="inline-flex items-center gap-1.5 mt-4 text-[#8A9A6B]" aria-hidden="true">
            {!open && <span className="text-[0.62rem] uppercase tracking-[0.28em]">Read</span>}
            <ChevronDown
              size={20}
              strokeWidth={1.5}
              className="transition-transform duration-500 ease-out group-hover:translate-y-0.5"
              style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
            />
          </span>
        </div>
      </button>

      <AnimatePresence initial={false}>
        {open && active && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.75, ease: [0.19, 1, 0.22, 1] },
              opacity: { duration: 0.55, ease: "easeInOut" },
            }}
            className="overflow-hidden"
          >
            <div data-testid="testimonial-full" className="relative px-2 pt-9 pb-4 text-center">
              <span className="pointer-events-none select-none block font-serif-lux text-[#8A9A6B]/25 text-8xl leading-[0.4] mb-6" aria-hidden="true">
                &ldquo;
              </span>
              <div className="flex justify-center gap-1.5 mb-6" aria-label={`${t.rating} out of 5 stars`}>
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} size={16} className="text-[#B8934A]" fill={s < t.rating ? "#B8934A" : "none"} strokeWidth={1.5} />
                ))}
              </div>
              <p className="text-[#2A2A2A] text-base leading-relaxed font-light max-w-lg mx-auto">{t.quote}</p>
              <span className="block mx-auto mt-8 w-10 h-px bg-[#B8934A]/50" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Testimonials() {
  const { content } = useContent();
  const list = Array.isArray(content.testimonials) ? content.testimonials : DEFAULT_TESTIMONIALS;
  const scrollerRef = useRef(null);
  const [active, setActive] = useState(0);

  const handleScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const center = el.scrollLeft + el.clientWidth / 2;
    let best = 0;
    let bestD = Infinity;
    Array.from(el.children).forEach((c, idx) => {
      const cc = c.offsetLeft + c.offsetWidth / 2;
      const d = Math.abs(cc - center);
      if (d < bestD) {
        bestD = d;
        best = idx;
      }
    });
    if (best !== active) setActive(best);
  };

  const scrollTo = (idx) => {
    const el = scrollerRef.current;
    if (!el) return;
    const child = el.children[idx];
    if (!child) return;
    el.scrollTo({ left: child.offsetLeft - (el.clientWidth - child.offsetWidth) / 2, behavior: "smooth" });
  };

  return (
    <section data-testid="testimonials-section" className="relative py-24 md:py-36 overflow-hidden">
      <img src="/botanical/corner-topleft.png" alt="" aria-hidden="true"
        className="hidden md:block absolute top-6 left-0 w-36 lg:w-48 opacity-20 pointer-events-none select-none z-10" />
      <img src="/botanical/corner-bottomright.png" alt="" aria-hidden="true"
        className="hidden md:block absolute bottom-4 right-0 w-36 lg:w-48 opacity-20 pointer-events-none select-none z-10" />

      <div className="relative">
        <div className="text-center mb-14 md:mb-20 container-lux">
          <h2 className="font-serif-lux text-[#47562F] text-4xl md:text-5xl font-light">Testimonials</h2>
          <span className="block mx-auto mt-5 w-16 h-px bg-[#B8934A]/60" />
        </div>

        <div
          ref={scrollerRef}
          onScroll={handleScroll}
          data-testid="testimonials-scroller"
          className="no-scrollbar flex gap-6 md:gap-8 overflow-x-auto snap-x snap-mandatory scroll-smooth px-[8%] md:px-[27%] items-start"
        >
          {list.map((t, idx) => (
            <div key={t.name + idx} className="snap-center shrink-0 w-[84%] sm:w-[62%] md:w-[46%] lg:w-[40%]">
              <TestimonialCard t={t} active={idx === active} onActivate={() => scrollTo(idx)} />
            </div>
          ))}
        </div>

        {list.length > 1 && (
          <div className="flex justify-center gap-2 mt-10">
            {list.map((_, d) => (
              <button
                key={d}
                data-testid={`testimonial-dot-${d}`}
                onClick={() => scrollTo(d)}
                aria-label={`Go to testimonial ${d + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${d === active ? "w-6 bg-[#B8934A]" : "w-1.5 bg-[#47562F]/25"}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
